import { Button } from "@dropbox/dig-components/buttons";
import { Chip } from "@dropbox/dig-components/chip";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { CursorLine, SearchLine } from "@dropbox/dig-icons/assets";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_SourceFile } from "@mirage/analytics/events/types/click_source_file";
import type { LayoutVariant } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/types";
import { generateSearchURL } from "@mirage/search/hooks/useQueryParams";
import { tagged } from "@mirage/service-logging";
import { openURL } from "@mirage/service-platform-actions";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import { FileContentIcon } from "@mirage/shared/icons/FileContentIcon";
import i18n from "@mirage/translations";
import { memo } from "react";
import type {
  ChatConversationMessageMessage,
  ChatSource,
} from "../../../types";
import { composeSourceToMirage } from "../../compose-sources/ComposeSourceRow";
import styles from "./ConversationMessages.module.css";

const logger = tagged("ConversationMessageActionContextView");

interface ConversationMessageActionContextViewProps {
  variant: LayoutVariant;
  message: ChatConversationMessageMessage;
  onOpenArtifact: () => void;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
}

/**
 * Render additional context about the action that was taken.
 */
export const ConversationMessageActionContextView = memo(
  ({
    variant,
    message,
    onOpenArtifact,
    logComposeEvent,
    conversationId,
    searchSessionId,
    assistRequestId,
  }: ConversationMessageActionContextViewProps) => {
    if (!message.actionContext) {
      return null;
    }
    switch (message.actionContext?.type) {
      case "dash_search": {
        return (
          <div>
            {message.actionContext.queries.map((query, i) => (
              <SearchQueryChip key={i} query={query} />
            ))}
          </div>
        );
      }
      case "dash_search_analyze":
        return (
          <div>
            {message.referencingSources?.map((source, i) => (
              <ComposeMessageSourceRowChip
                key={i}
                source={source}
                logComposeEvent={logComposeEvent}
                conversationId={conversationId}
                searchSessionId={searchSessionId}
                assistRequestId={assistRequestId}
              />
            ))}
          </div>
        );
      case "compose_selection_edit":
        return (
          <div className={styles.ComposeSelectionEditContext}>
            <UIIcon
              src={CursorLine}
              size="small"
              className={styles.ComposeSelectionEditContextIcon}
            />
            <Text size="small" color="subtle" variant="label">
              {`"${message.actionContext.selectedText}"`}
            </Text>
          </div>
        );
      case "draft_generated":
        // only need to show this in Condensed view -- normal layout has the artifact pane on the side
        return variant === "condensed" ? (
          <Button variant="opacity" size="small" onClick={onOpenArtifact}>
            {i18n.t("compose_open_draft")}
          </Button>
        ) : null;
      case "done":
      case "error":
      case "reading":
      case "read_current_browser_tab":
      case "template_generated":
      case "template_updated":
      case "template_selected":
      case "tone_generated":
      case "tone_updated":
      case "tone_selected":
        return null;
      default:
        message.actionContext satisfies never;
        throw new Error(
          `Unknown action context type: ${message.actionContext}`,
        );
    }
  },
);
ConversationMessageActionContextView.displayName =
  "ConversationMessageActionContextView";

interface SearchQueryChipProps {
  query: string;
}

export const SearchQueryChip = memo(({ query }: SearchQueryChipProps) => {
  const handleClick = () => {
    const urlString = generateSearchURL(query);
    const url = new URL(urlString, document.baseURI);
    openURL(url.toString());
  };
  return (
    <Chip
      variant="standard"
      type="button"
      size="medium"
      className={styles.SearchQueryChip}
      onClick={handleClick}
    >
      <Chip.IconAccessory>
        <UIIcon size="small" src={SearchLine} />
      </Chip.IconAccessory>
      <Chip.Content className={styles.ChipContent}>{query}</Chip.Content>
    </Chip>
  );
});
SearchQueryChip.displayName = "SearchQueryChip";

interface ComposeMessageSourceRowChipProps {
  source: ChatSource;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
}
export const ComposeMessageSourceRowChip = memo(
  ({
    source,
    logComposeEvent,
    conversationId,
    searchSessionId,
    assistRequestId,
  }: ComposeMessageSourceRowChipProps) => {
    const mirageResult = composeSourceToMirage(source);
    const { searchQueryUuid } = useSearchQueryId();
    if (!mirageResult) {
      return null;
    }
    const handleClick = () => {
      const url = mirageResult.url;
      if (url && source.type !== "local_file_content") {
        openURL(url);
        logComposeEvent(
          PAP_Click_SourceFile({
            actionType: "search_result",
            conversationId,
            searchSessionId,
            searchQueryUuid,
            assistRequestId,
            resultUuid: mirageResult.uuid,
          }),
        );
      } else {
        logger.error("No URL found for source", source);
      }
    };

    return (
      <Chip
        variant="standard"
        type="button"
        size="medium"
        className={styles.SearchResultChip}
        onClick={handleClick}
      >
        <Chip.IconAccessory>
          <div className={styles.SearchResultChipIcon}>
            <FileContentIcon
              content={mirageResult!}
              size="small"
              hasBackground={false}
            />
          </div>
        </Chip.IconAccessory>
        <Chip.Content className={styles.ChipContent}>
          <Truncate lines={1}>{mirageResult.title}</Truncate>
        </Chip.Content>
      </Chip>
    );
  },
);
ComposeMessageSourceRowChip.displayName = "ComposeMessageSourceRowChip";
